import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AdminIcon } from "@/components/admin/icon";

export interface MultiSelectOption {
	value: string;
	label: string;
	badge?: string;
}

interface MultiSelectProps {
	options: MultiSelectOption[];
	selectedValues: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	emptyMessage?: string;
	disabled?: boolean;
}

export function MultiSelect({
	options,
	selectedValues,
	onChange,
	placeholder = "Sélectionner des éléments...",
	searchPlaceholder = "Rechercher...",
	emptyMessage = "Aucun élément trouvé",
	disabled = false,
}: MultiSelectProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [popoverPos, setPopoverPos] = useState<{
		top?: number;
		bottom?: number;
		left: number;
		width: number;
		maxHeight: number;
		openUpwards: boolean;
	} | null>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const updatePosition = useCallback(() => {
		if (!containerRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const spaceAbove = rect.top;
		const openUpwards = spaceBelow < 280 && spaceAbove > spaceBelow;

		if (openUpwards) {
			const maxHeight = Math.min(300, Math.max(160, spaceAbove - 24));
			setPopoverPos({
				bottom: window.innerHeight - rect.top + 6,
				left: rect.left,
				width: rect.width,
				maxHeight,
				openUpwards: true,
			});
		} else {
			const maxHeight = Math.min(300, Math.max(160, spaceBelow - 24));
			setPopoverPos({
				top: rect.bottom + 6,
				left: rect.left,
				width: rect.width,
				maxHeight,
				openUpwards: false,
			});
		}
	}, []);

	// Fermeture au clic à l'extérieur & positionnement dynamique via Portal
	useEffect(() => {
		if (!isOpen) return;

		updatePosition();

		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				const popoverEl = document.getElementById("multiselect-popover-portal");
				if (popoverEl?.contains(event.target as Node)) {
					return;
				}
				setIsOpen(false);
			}
		};

		const handleScrollOrResize = () => {
			updatePosition();
		};

		document.addEventListener("mousedown", handleClickOutside);
		window.addEventListener("scroll", handleScrollOrResize, true);
		window.addEventListener("resize", handleScrollOrResize);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("scroll", handleScrollOrResize, true);
			window.removeEventListener("resize", handleScrollOrResize);
		};
	}, [isOpen, updatePosition]);

	// Focus automatique sur le champ de recherche à l'ouverture
	useEffect(() => {
		if (isOpen) {
			setTimeout(() => {
				searchInputRef.current?.focus();
			}, 50);
		} else {
			setSearchQuery("");
		}
	}, [isOpen]);

	const filteredOptions = options.filter((opt) =>
		opt.label.toLowerCase().includes(searchQuery.toLowerCase().trim()),
	);

	const isAllSelected =
		filteredOptions.length > 0 &&
		filteredOptions.every((opt) => selectedValues.includes(opt.value));

	const handleToggleOption = (value: string) => {
		if (disabled) return;
		if (selectedValues.includes(value)) {
			onChange(selectedValues.filter((v) => v !== value));
		} else {
			onChange([...selectedValues, value]);
		}
	};

	const handleRemoveChip = (e: React.MouseEvent, value: string) => {
		e.stopPropagation();
		if (disabled) return;
		onChange(selectedValues.filter((v) => v !== value));
	};

	const handleClearAll = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (disabled) return;
		onChange([]);
	};

	const handleSelectAllFiltered = () => {
		if (disabled) return;
		const filteredValues = filteredOptions.map((o) => o.value);
		if (isAllSelected) {
			// Décocher ceux du filtre actuel
			onChange(selectedValues.filter((v) => !filteredValues.includes(v)));
		} else {
			// Tout cocher dans le filtre actuel sans doublons
			const newSet = new Set([...selectedValues, ...filteredValues]);
			onChange(Array.from(newSet));
		}
	};

	const selectedOptions = options.filter((opt) =>
		selectedValues.includes(opt.value),
	);

	return (
		<div ref={containerRef} className="relative w-full text-left font-sans">
			{/* Boîtier principal d'activation (Trigger Box) */}
			<div
				tabIndex={disabled ? -1 : 0}
				role="combobox"
				aria-expanded={isOpen}
				onClick={() => !disabled && setIsOpen(!isOpen)}
				onKeyDown={(e) => {
					if ((e.key === "Enter" || e.key === " ") && !disabled) {
						e.preventDefault();
						setIsOpen(!isOpen);
					}
				}}
				style={{
					borderColor: isOpen ? "var(--a-terracotta)" : undefined,
					boxShadow: isOpen ? "0 0 0 3px rgba(198, 86, 58, 0.18)" : undefined,
				}}
				className={`min-h-10.5 w-full rounded-lg border bg-white px-3 py-1.5 transition-all flex items-center justify-between gap-2 cursor-pointer text-left ${
					disabled
						? "bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed"
						: isOpen
							? "border-[var(--a-terracotta)]"
							: "border-slate-300 hover:border-slate-400"
				}`}
			>
				{/* Conteneur des chips avec hauteur maximale restreinte */}
				<div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 py-0.5 max-h-24 overflow-y-auto pr-1">
					{selectedOptions.length === 0 ? (
						<span className="text-xs text-slate-400 font-normal select-none">
							{placeholder}
						</span>
					) : isAllSelected && options.length > 2 ? (
						<span
							style={{
								background: "var(--a-terra-soft)",
								borderColor: "rgba(198, 86, 58, 0.3)",
								color: "var(--a-terra-deep)",
							}}
							className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-0.5 text-xs font-bold shadow-xs"
						>
							<span>Tous les éléments ({options.length})</span>
							{!disabled && (
								<button
									type="button"
									onClick={handleClearAll}
									title="Tout désélectionner"
									className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[var(--a-terra-deep)] hover:bg-[var(--a-terracotta)]/20 transition-colors"
								>
									<AdminIcon n="close" s={10} />
								</button>
							)}
						</span>
					) : selectedOptions.length > 4 ? (
						<>
							{selectedOptions.slice(0, 3).map((opt) => (
								<span
									key={opt.value}
									style={{
										background: "var(--a-terra-soft)",
										borderColor: "rgba(198, 86, 58, 0.3)",
										color: "var(--a-terra-deep)",
									}}
									className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold shadow-xs"
								>
									<span className="truncate max-w-35">{opt.label}</span>
									{!disabled && (
										<button
											type="button"
											onClick={(e) => handleRemoveChip(e, opt.value)}
											aria-label={`Supprimer ${opt.label}`}
											className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[var(--a-terra-deep)] hover:bg-[var(--a-terracotta)]/20 transition-colors"
										>
											<AdminIcon n="close" s={10} />
										</button>
									)}
								</span>
							))}
							<span
								title={selectedOptions
									.slice(3)
									.map((o) => o.label)
									.join(", ")}
								className="inline-flex items-center gap-1 rounded-md bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 text-xs font-bold shadow-2xs"
							>
								+{selectedOptions.length - 3} autres
							</span>
						</>
					) : (
						selectedOptions.map((opt) => (
							<span
								key={opt.value}
								style={{
									background: "var(--a-terra-soft)",
									borderColor: "rgba(198, 86, 58, 0.3)",
									color: "var(--a-terra-deep)",
								}}
								className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold shadow-xs animate-in fade-in zoom-in-95 duration-100"
							>
								<span className="truncate max-w-40">{opt.label}</span>
								{!disabled && (
									<button
										type="button"
										onClick={(e) => handleRemoveChip(e, opt.value)}
										aria-label={`Supprimer ${opt.label}`}
										className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[var(--a-terra-deep)] hover:bg-[var(--a-terracotta)]/20 transition-colors"
									>
										<AdminIcon n="close" s={10} />
									</button>
								)}
							</span>
						))
					)}
				</div>

				{/* Boutons d'action à droite (Bouton Effacer + Flèche) */}
				<div className="flex items-center gap-1.5 shrink-0 text-slate-400 pl-1">
					{selectedValues.length > 0 && !disabled && (
						<button
							type="button"
							onClick={handleClearAll}
							title="Tout désélectionner"
							className="p-1 text-slate-400 hover:text-rose-600 transition-colors rounded-full hover:bg-slate-100"
						>
							<AdminIcon n="close" s={12} />
						</button>
					)}
					<div className="h-4 w-px bg-slate-200" />
					<div
						className={`transition-transform duration-200 ${
							isOpen ? "rotate-180 text-[var(--a-terracotta)]" : ""
						}`}
					>
						<AdminIcon n="chevD" s={14} />
					</div>
				</div>
			</div>

			{/* Panneau déroulant rendu dans document.body via Portal (Anti-clipping) */}
			{isOpen &&
				popoverPos &&
				createPortal(
					<div
						id="multiselect-popover-portal"
						style={{
							position: "fixed",
							left: popoverPos.left,
							width: popoverPos.width,
							top: popoverPos.top,
							bottom: popoverPos.bottom,
							maxHeight: popoverPos.maxHeight,
							zIndex: 1100,
						}}
						className={`flex flex-col rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${
							popoverPos.openUpwards
								? "animate-in fade-in slide-in-from-bottom-2 duration-150"
								: "animate-in fade-in slide-in-from-top-2 duration-150"
						}`}
					>
						{/* Barre de recherche sticky */}
						<div className="p-2 border-b border-slate-100 bg-slate-50/50 shrink-0">
							<div className="relative flex items-center">
								<div className="absolute left-2.5 text-slate-400 pointer-events-none flex items-center">
									<AdminIcon n="search" s={13} />
								</div>
								<input
									ref={searchInputRef}
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder={searchPlaceholder}
									style={{
										borderColor: "var(--a-border)",
									}}
									className="w-full rounded-md border bg-white py-1.5 pl-8 pr-7 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:border-[var(--a-terracotta)] focus:outline-none focus:ring-1 focus:ring-[var(--a-terracotta)]"
								/>
								{searchQuery && (
									<button
										type="button"
										onClick={() => setSearchQuery("")}
										className="absolute right-2 text-slate-400 hover:text-slate-600"
									>
										<AdminIcon n="close" s={12} />
									</button>
								)}
							</div>

							{/* Barre d'action rapide (Tout cocher / Effacer / Compteur) */}
							<div className="mt-2 flex items-center justify-between px-1 text-[11px] font-medium text-slate-500">
								<button
									type="button"
									onClick={handleSelectAllFiltered}
									className="flex items-center gap-1.5 hover:text-[var(--a-terracotta)] transition-colors cursor-pointer"
								>
									<input
										type="checkbox"
										checked={isAllSelected}
										onChange={() => {}}
										style={{ accentColor: "var(--a-terracotta)" }}
										className="h-3.5 w-3.5 rounded cursor-pointer pointer-events-none"
									/>
									<span>
										{isAllSelected ? "Tout décocher" : "Tout sélectionner"}
									</span>
								</button>

								<span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
									{selectedValues.length} / {options.length} sélectionnée(s)
								</span>
							</div>
						</div>

						{/* Liste scrollable des options */}
						<div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-1 min-h-0">
							{filteredOptions.length === 0 ? (
								<div className="py-6 text-center text-xs text-slate-400 font-normal">
									{emptyMessage}
								</div>
							) : (
								filteredOptions.map((opt) => {
									const isSelected = selectedValues.includes(opt.value);
									return (
										<button
											type="button"
											key={opt.value}
											onClick={() => handleToggleOption(opt.value)}
											style={{
												background: isSelected ? "var(--a-terra-soft)" : undefined,
												color: isSelected ? "var(--a-terra-deep)" : undefined,
											}}
											className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-xs transition-colors cursor-pointer select-none text-left border-none ${
												isSelected
													? "font-semibold"
													: "text-slate-700 hover:bg-slate-50"
											}`}
										>
											<div className="flex items-center gap-2.5 min-w-0">
												<input
													type="checkbox"
													checked={isSelected}
													onChange={() => {}}
													style={{ accentColor: "var(--a-terracotta)" }}
													className="h-4 w-4 rounded border-slate-300 cursor-pointer pointer-events-none"
												/>
												<span className="truncate">{opt.label}</span>
											</div>

											{opt.badge && (

												<span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
													{opt.badge}
												</span>
											)}
										</button>
									);
								})
							)}
						</div>

						{/* Pied de popover */}
						<div className="border-t border-slate-100 bg-slate-50 p-2 flex items-center justify-between shrink-0">
							<span className="text-[11px] text-slate-500">
								{selectedValues.length === 0
									? "Aucun filtre actif (Tous autorisés)"
									: `${selectedValues.length} élément(s) sélectionné(s)`}
							</span>
							<button
								type="button"
								onClick={() => setIsOpen(false)}
								className="rounded-md bg-white border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs"
							>
								Fermer
							</button>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
}
